/**
 * Canonical 0–100 strategy / factor rating tiers.
 *
 * Labels:  85 / 67 / 34 / 15 → VERY STRONG / STRONG / AVERAGE / WEAK / VERY WEAK
 * Colors:  ≥67 green · ≥34 yellow · else red  (STRONG is always green)
 *
 * Momentum / heatmap bars keep a finer 1–2–3 fill inside each color band.
 * ≥85 ALWAYS fills 3 bars.
 */

export const RATING_TIER_GREEN = '#4ade80';
export const RATING_TIER_YELLOW = '#facc15';
export const RATING_TIER_RED = '#f87171';

export type RatingTierId =
  | 'very_strong'
  | 'strong'
  | 'average'
  | 'weak'
  | 'very_weak'
  | 'none';

/** Score bands for gauges / legends. End is exclusive except the last (100). */
export const RATING_TIER_BANDS: ReadonlyArray<{
  id: Exclude<RatingTierId, 'none'>;
  from: number;
  to: number;
  color: string;
}> = [
  { id: 'very_weak', from: 0, to: 15, color: RATING_TIER_RED },
  { id: 'weak', from: 15, to: 34, color: RATING_TIER_RED },
  { id: 'average', from: 34, to: 67, color: RATING_TIER_YELLOW },
  { id: 'strong', from: 67, to: 85, color: RATING_TIER_GREEN },
  { id: 'very_strong', from: 85, to: 100, color: RATING_TIER_GREEN },
];

function clamp01to100(score: number): number {
  return Math.max(0, Math.min(100, score));
}

/** Missing / zero scores are treated as no rating (UI muted). */
export function ratingTierId(score: number | null | undefined): RatingTierId {
  if (score == null || !Number.isFinite(Number(score)) || Number(score) === 0) return 'none';
  const s = clamp01to100(Number(score));
  if (s >= 85) return 'very_strong';
  if (s >= 67) return 'strong';
  if (s >= 34) return 'average';
  if (s >= 15) return 'weak';
  return 'very_weak';
}

/** Uppercase labels for stock Briefs / sidebars. */
export function scoreTierLabel(score: number | null | undefined): string {
  switch (ratingTierId(score)) {
    case 'very_strong':
      return 'VERY STRONG';
    case 'strong':
      return 'STRONG';
    case 'average':
      return 'AVERAGE';
    case 'weak':
      return 'WEAK';
    case 'very_weak':
      return 'VERY WEAK';
    default:
      return '';
  }
}

/** Title Case for Radar / playbook verdict columns. */
export function scoreTierVerdictLabel(score: number | null | undefined): string {
  switch (ratingTierId(score)) {
    case 'very_strong':
      return 'Very Strong';
    case 'strong':
      return 'Strong';
    case 'average':
      return 'Average';
    case 'weak':
      return 'Weak';
    case 'very_weak':
      return 'Very Weak';
    default:
      return '';
  }
}

/** Text + soft bg paired with the tier label (STRONG+ → green). */
export function scoreTierColors(score: number | null | undefined): { text: string; bg: string } {
  const tier = ratingTierId(score);
  if (tier === 'none') {
    return { text: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.04)' };
  }
  if (tier === 'very_strong' || tier === 'strong') {
    return { text: RATING_TIER_GREEN, bg: '#1a2e1a' };
  }
  if (tier === 'average') {
    return { text: RATING_TIER_YELLOW, bg: '#2e2a14' };
  }
  return { text: RATING_TIER_RED, bg: '#2e1a1a' };
}

export function scoreTierColor(score: number | null | undefined): string {
  return scoreTierColors(score).text;
}

/**
 * 3-bar fill inside the canonical color bands.
 *
 * Green  (≥67): ≥85 → 3 · ≥76 → 2 · ≥67 → 1
 * Yellow (≥34): ≥56 → 3 · ≥45 → 2 · ≥34 → 1
 * Red    (<34): ≥25 → 3 · ≥15 → 2 · else 1
 */
export function ratingMomentumBars(
  score: number | null | undefined,
): { filled: number; color: string } {
  const raw =
    score == null || !Number.isFinite(Number(score))
      ? 0
      : clamp01to100(Number(score));
  const color = scoreTierColor(raw > 0 ? raw : null);

  let filled: number;
  if (raw >= 67) {
    filled = raw >= 85 ? 3 : raw >= 76 ? 2 : 1;
  } else if (raw >= 34) {
    filled = raw >= 56 ? 3 : raw >= 45 ? 2 : 1;
  } else {
    filled = raw >= 25 ? 3 : raw >= 15 ? 2 : 1;
  }

  return { filled, color: raw > 0 ? color : RATING_TIER_RED };
}
